import { createAsyncThunk, createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import {
  BookingMethods,
  IBooking,
  IBookingMenuOption,
  IPaymentDetailsBase,
  IServiceItem,
  PricingTypes,
  servicePaymentType,
  BookingStateChangeTypes,
  preAuthStatus,
  IBookingFromPouch,
  BookingType,
  DiaryMobileVersion,
} from 'shared-types/index';
import { BookingMode } from 'booking-creator/reducers/display/types';
import PaymentService from 'external-services/diary-payment-service';
import CustomerNotificationService from 'external-services/customer-notification-service/customerNotification.service';
import { DiaryRootState } from 'app/appRoot/rootReducer';
import { displayActions } from 'booking-creator/reducers/display/slice';
import { IPaymentState, PreAuthState } from './types';
import { cloneDeep } from 'lodash-es';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import PreAuthService from 'booking-creator/services/pre-auth-service';
import BookingChangeService from 'external-services/booking-change-service/bookingChange.service';
import { UrlGeneratorService } from 'external-services/url-generator-service/UrlGenerator.service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';

const NS = 'paymentSlice';

//TODO: update values for initial state
const initialState: IPaymentState = {
  isChangeByUser: false,
  paymentDetailsChanged: false,
  isPaymentTypeChanged: false,
  isPriceChanged: false,
  originalPaymentDetails: {} as IPaymentDetailsBase,
  preAuthState: 'view',
  preAuthErrorMsg: '',
  paymentUrl: '',
  preAuthDialog: false,
  isSetActiveBookingInBookingCreator: true,
  isPaymentPriceDirty: false,
};

export const getActivePaymentDetail = (
  selectedService: IServiceItem,
  people: number,
  selectedDate: Date
): IPaymentDetailsBase | null => {
  let activePaymentDetails: IPaymentDetailsBase;

  if (selectedService) {
    const paymentDetails = cloneDeep(selectedService.paymentDetails);
    const additionalPaymentDetails = cloneDeep(selectedService.additionalPaymentDetails);

    PaymentService.populateDefaultPricingTypes(paymentDetails as any, additionalPaymentDetails);

    activePaymentDetails = PaymentService.getPaymentDetailsByServiceIgnorePax(
      people,
      paymentDetails as any,
      additionalPaymentDetails,
      selectedDate
    );
  }

  return activePaymentDetails;
};

const getPaymentDetailChanged = (
  paymentDetails: IPaymentDetailsBase,
  originalPaymentDetails: IPaymentDetailsBase
): boolean => {
  const result =
    paymentDetails.paymentType !== originalPaymentDetails?.paymentType ||
    (paymentDetails.price && paymentDetails.price !== originalPaymentDetails?.price);

  return result;
};

export const updatePaymentBasedOnBookingOptions = createAsyncThunk(
  'payment/updatePaymentBasedOnBookingOptions',
  async (
    payload: { selectedMenuOptions: IBookingMenuOption[]; isUpsell?: boolean },
    { dispatch, getState }
  ): Promise<{
    paymentDetailsChanged: boolean;
    isPaymentTypeChanged: boolean;
    isPriceChanged: boolean;
    originalPaymentDetails: IPaymentDetailsBase;
  } | null> => {
    const { displayReducer, bookingTimesReducer, paymentReducer } = getState() as DiaryRootState;
    const booking = displayReducer.activeBooking;
    const originalPaymentDetails = paymentReducer.originalPaymentDetails;
    let isPaymentTypeChanged = paymentReducer.isPaymentTypeChanged;
    let isPriceChanged = paymentReducer.isPriceChanged;

    const newSelectedMenuOption = payload.selectedMenuOptions.filter((item) => item.quantity > 0);
    let paymentInfo: { paymentType: servicePaymentType; price: number };
    try {
      paymentInfo = await PaymentService.getBookingPaymentDetailsFromServiceAndBookingOptions(
        newSelectedMenuOption,
        booking.serviceId,
        booking.time,
        bookingTimesReducer.availableTimes,
        booking.people,
        payload.isUpsell
      ).toPromise();
    } catch (e) {
      console.warn(
        NS,
        'PaymentService.getBookingPaymentDetailsFromServiceAndBookingOptions error',
        e
      );
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Booking Options payment calculation error',
          errorMessage: `There was an error while getting payment details from these booking options. Payment calculations may not be accurate. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(e),
        })
      );
    }
    const newPaymentDetails = {
      ...booking.paymentDetails,
      paymentType: paymentInfo.paymentType,
      price: paymentInfo.price,
    };
    await dispatch(displayActions.setPaymentDetails(newPaymentDetails));
    const res = setPaymentChangeMessage(
      newPaymentDetails,
      originalPaymentDetails,
      isPaymentTypeChanged,
      isPriceChanged
    );

    isPaymentTypeChanged = res.isPaymentTypeChanged;
    isPriceChanged = res.isPriceChanged;
    const paymentDetailsChanged = res.paymentDetailsChanged;

    return {
      isPaymentTypeChanged,
      isPriceChanged,
      paymentDetailsChanged,
      originalPaymentDetails: newPaymentDetails,
    };
  }
);

export const handleUpdatePreAuth = createAsyncThunk(
  'display/handleUpdatePreAuth',
  async (
    payload: { amount: number; isRelease: boolean },
    { dispatch, getState }
  ): Promise<void> => {
    const {
      displayReducer: { activeBooking },
      venueReducer,
    } = getState() as DiaryRootState;
    const { amount, isRelease } = payload;

    // Guard online status
    if (!NetworkConnectionService.isOnline()) {
      dispatch(actions.setPreAuthState('offline'));
      return;
    }

    // Amount is requied for charge
    if (!isRelease && !amount) {
      dispatch(actions.setPreAuthState('error'));
      dispatch(actions.setPreAuthErrorMsg('Amount is required'));

      return;
    }

    if (amount && amount > Number(activeBooking?.paymentSummary?.amountHeld)) {
      dispatch(actions.setPreAuthState('error'));
      dispatch(
        actions.setPreAuthErrorMsg(`Maximum amount is ${activeBooking?.paymentSummary?.amountHeld}`)
      );

      return;
    }

    dispatch(actions.setPreAuthState('loading'));

    PreAuthService.charge(venueReducer.id, activeBooking._id, activeBooking.customerId, amount)
      .then((response) => {
        console.log(NS, 'response', response);
        let changeType: BookingStateChangeTypes;
        if (amount) {
          dispatch(actions.setPreAuthState('charged'));
          dispatch(displayActions.updatePreAuthStatus(preAuthStatus.completelyCharged));
          changeType = BookingStateChangeTypes.PRE_AUTH_CHARGED;
        } else {
          dispatch(actions.setPreAuthState('released'));
          dispatch(displayActions.updatePreAuthStatus(preAuthStatus.released));
          changeType = BookingStateChangeTypes.PRE_AUTH_RELEASED;
        }
        // Track booking changes
        BookingChangeService.saveBookingChange(
          changeType,
          activeBooking as IBooking,
          null,
          '',
          amount
        );
      })
      .catch(function (err) {
        dispatch(actions.setPreAuthState('error'));
        dispatch(
          actions.setPreAuthErrorMsg(
            err && err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : ''
          )
        );
        console.warn(NS, 'PreAuthService.charge error', err);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'PreAuth charge error',
            errorMessage: `There was an error while charging PreAuth. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(err),
          })
        );
      });
  }
);

export const handlePaymentDetailsChange = createAsyncThunk(
  'display/handlePaymentDetailsChange',
  async (
    payload: { servicePaymentType?: servicePaymentType; price?: number },
    { dispatch, getState }
  ): Promise<void> => {
    const { paymentReducer } = getState() as DiaryRootState;
    const originalPaymentDetails = paymentReducer.originalPaymentDetails;
    let newPaymentDetails = {} as IPaymentDetailsBase;
    if (payload.servicePaymentType) {
      if (payload.servicePaymentType === servicePaymentType.noPayment) {
        newPaymentDetails = {
          ...originalPaymentDetails,
          paymentType: payload.servicePaymentType,
          price: 0,
        };
      } else {
        newPaymentDetails = { ...originalPaymentDetails, paymentType: payload.servicePaymentType };
      }
    } else if (payload.price) {
      newPaymentDetails = { ...originalPaymentDetails, price: payload.price };
    }
    dispatch(actions.setOriginalPaymentDetails(newPaymentDetails));
    dispatch(actions.setIsChangeByUser(true));
    dispatch(actions.setPaymentDetailsChanged(false));
  }
);

const setPaymentChangeMessage = (
  paymentDetails: IPaymentDetailsBase,
  originalPaymentDetails: IPaymentDetailsBase,
  isPaymentTypeChanged: boolean,
  isPriceChanged: boolean
): {
  isPaymentTypeChanged: boolean;
  isPriceChanged: boolean;
  paymentDetailsChanged: boolean;
} => {
  const paymentDetailsChanged = getPaymentDetailChanged(paymentDetails, originalPaymentDetails);

  if (paymentDetailsChanged) {
    return {
      isPaymentTypeChanged: paymentDetails.paymentType !== originalPaymentDetails?.paymentType,
      isPriceChanged: paymentDetails.price !== originalPaymentDetails?.price,
      paymentDetailsChanged,
    };
  }

  return {
    isPaymentTypeChanged,
    isPriceChanged,
    paymentDetailsChanged,
  };
};

export const sendLinkPayment = createAsyncThunk(
  'payment/sendingLinkPayment',
  async (
    payload: {
      method: 'sms' | 'normal-way';
      booking: IBooking;
      newValue: string; // either Phone or Email,
      saveValueToCustomer: boolean;
      paymentUrl: string;
      bookedByNotification?: boolean;
    },
    { dispatch }
  ): Promise<boolean> => {
    const { method, booking, newValue, saveValueToCustomer, paymentUrl, bookedByNotification } =
      payload;

    const asyncFunction =
      method === 'sms'
        ? CustomerNotificationService.sendSmsLinkPayment.bind(CustomerNotificationService)
        : CustomerNotificationService.sendLinkPayment.bind(CustomerNotificationService);

    return new Promise(async (resolve, reject) => {
      try {
        const res = await asyncFunction(
          booking,
          newValue,
          saveValueToCustomer,
          paymentUrl,
          bookedByNotification
        );
        if (res) {
          resolve(true);
        } else {
          reject(false);
        }
      } catch (error) {
        console.warn(NS, 'payment/sendingLinkPayment error', error);
        dispatch(
          errorStateActions.setErrorState({
            errorTitle: 'Payment link error',
            errorMessage: `There was an error while sending payment link. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
            accordionDetail: getAccordionDetail(error),
          })
        );
        reject(false);
      }
    });
  }
);

export const setPaymentUrl = (booking: IBookingFromPouch | IBooking) => (dispatch: Dispatch) => {
  // Reset current state to make sure payment url is always correct
  dispatch(paymentSlice.actions.updatePaymentUrl(''));

  if (
    booking.bookingType === BookingType.Booking &&
    booking.paymentDetails &&
    booking.paymentDetails.paymentType !== servicePaymentType.noPayment &&
    (!booking.paymentSummary || !booking.paymentSummary.transactionId)
  ) {
    const paymentUrl = UrlGeneratorService.generateBookingUrl(booking._id, true);
    dispatch(paymentSlice.actions.updatePaymentUrl(paymentUrl));
  }
};

export const prefillPayment = createAsyncThunk(
  'payment/prefillPayment',
  (
    payload: { selectedService: IServiceItem; selectedPeople: number },
    { dispatch, getState }
  ): {
    isChangeByUser: boolean;
    paymentDetailsChanged: boolean;
    isPaymentTypeChanged: boolean;
    isPriceChanged: boolean;
    originalPaymentDetails: IPaymentDetailsBase;
  } | null => {
    const { displayReducer, scheduleReducer, paymentReducer, settingReducer, venueReducer } =
      getState() as DiaryRootState;
    const isPlusVersion = settingReducer.mobileVersion === DiaryMobileVersion.PlusVersion;
    const isNewBooking = displayReducer.mode === BookingMode.newBooking;

    if (
      !isNewBooking ||
      !payload.selectedService ||
      displayReducer.activeBooking.method === BookingMethods.walkin
    ) {
      return null;
    }

    const { isBookingLoaded } = displayReducer;
    const { selectedDate } = scheduleReducer;
    let {
      isPaymentTypeChanged,
      isPriceChanged,
      isChangeByUser,
      paymentDetailsChanged,
      originalPaymentDetails,
    } = paymentReducer;

    const activePaymentDetails = getActivePaymentDetail(
      payload.selectedService,
      payload.selectedPeople,
      selectedDate
    );

    const isPaxThresholdValid = activePaymentDetails.peopleRequired <= payload.selectedPeople;
    const isPriceAndBoAvailable = PaymentService.isPriceAndBoAvailable(activePaymentDetails);
    if (
      activePaymentDetails &&
      activePaymentDetails.price &&
      isPaxThresholdValid &&
      !isPriceAndBoAvailable &&
      isPlusVersion
    ) {
      const price =
        activePaymentDetails.pricingType === PricingTypes.PerBooking
          ? activePaymentDetails.price
          : Number.parseFloat((activePaymentDetails.price * payload.selectedPeople).toFixed(2));

      dispatch(displayActions.setPaymentType(activePaymentDetails.paymentType));
      dispatch(displayActions.setPaymentPrice(price));
      dispatch(displayActions.setPricingType(activePaymentDetails.pricingType));
    } else {
      dispatch(displayActions.setPaymentType(servicePaymentType.noPayment));
    }

    if (isBookingLoaded) {
      const activeBooking = (getState() as DiaryRootState).displayReducer.activeBooking;
      if (!isChangeByUser) {
        originalPaymentDetails = cloneDeep(activeBooking.paymentDetails);
        isChangeByUser = true;
      } else {
        const res = setPaymentChangeMessage(
          activeBooking.paymentDetails,
          originalPaymentDetails,
          isPaymentTypeChanged,
          isPriceChanged
        );

        isPaymentTypeChanged = res.isPaymentTypeChanged;
        isPriceChanged = res.isPriceChanged;
        paymentDetailsChanged = res.paymentDetailsChanged;
      }
    }

    return {
      isChangeByUser,
      isPaymentTypeChanged,
      isPriceChanged,
      paymentDetailsChanged,
      originalPaymentDetails,
    };
  }
);
export const _unitTests = () => ({
  setPaymentChangeMessage,
  getPaymentDetailChanged,
});
const paymentSlice = createSlice({
  name: 'payment', // used in action types
  initialState,
  reducers: {
    setIsChangeByUser: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.isChangeByUser = action.payload;
    },
    setIsPaymentTypeChanged: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.isPaymentTypeChanged = action.payload;
    },
    setIsPriceChanged: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.isPriceChanged = action.payload;
    },
    setPreAuthState: (state: IPaymentState, action: PayloadAction<PreAuthState>) => {
      state.preAuthState = action.payload;
    },
    setPreAuthErrorMsg: (state: IPaymentState, action: PayloadAction<string>) => {
      state.preAuthErrorMsg = action.payload;
    },
    resetPreAuthState: (state: IPaymentState) => {
      state.preAuthState = 'view';
      state.preAuthErrorMsg = '';
    },
    updatePaymentUrl: (state: IPaymentState, action: PayloadAction<string>) => {
      state.paymentUrl = action.payload;
    },
    setIsPaymentPriceDirty: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.isPaymentPriceDirty = action.payload;
    },
    setOriginalPaymentDetails: (
      state: IPaymentState,
      action: PayloadAction<IPaymentDetailsBase>
    ) => {
      state.originalPaymentDetails = action.payload;
    },
    setPaymentDetailsChanged: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.paymentDetailsChanged = action.payload;
    },
    setPreAuthDialog: (state: IPaymentState, action: PayloadAction<boolean>) => {
      state.preAuthDialog = action.payload;
    },
    setIsSetActiveBookingInBookingCreator: (
      state: IPaymentState,
      action: PayloadAction<boolean>
    ) => {
      state.isSetActiveBookingInBookingCreator = action.payload;
    },
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(prefillPayment.fulfilled, (state: IPaymentState, action) => {
      state.isChangeByUser = action.payload?.isChangeByUser || false;
      state.paymentDetailsChanged = action.payload?.paymentDetailsChanged || false;
      state.isPaymentTypeChanged = action.payload?.isPaymentTypeChanged || false;
      state.isPriceChanged = action.payload?.isPriceChanged || false;
      state.originalPaymentDetails = action.payload?.originalPaymentDetails;
    });
    builder.addCase(
      updatePaymentBasedOnBookingOptions.fulfilled,
      (state: IPaymentState, action) => {
        state.paymentDetailsChanged = action.payload?.paymentDetailsChanged || false;
        state.isPaymentTypeChanged = action.payload?.isPaymentTypeChanged || false;
        state.isPriceChanged = action.payload?.isPriceChanged || false;
        state.originalPaymentDetails = action.payload?.originalPaymentDetails;
      }
    );
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = paymentSlice;
// Extract and export each action creator by name
export { actions as paymentActions };
// Export the reducer, either as a default or named export
export default reducer;
